import type { PaScore } from '@/api/eos/peopleAnalyzer.api';
import type { SystemRole } from '@/types';

/**
 * Presentational metadata shared by the seat chart components. Pure, so
 * the chart can render seats + a GWC indicator without duplicating this
 * logic and the aggregation stays unit-testable.
 */

export const ROLE_LABEL: Record<SystemRole, string> = {
  visionary: 'Visionary',
  integrator: 'Integrator',
  department_lead: 'Department Lead',
  employee: 'Team Member',
  quality_lead: 'Quality Lead',
  qa_personnel: 'QA Personnel',
  auditor_readonly: 'Auditor',
};

export function roleLabel(role: SystemRole): string {
  return ROLE_LABEL[role];
}

// ── GWC (People Analyzer, get-it / want-it / capacity) ────────────────────

/** Per-key verdict for a seat holder: true = mostly yes, false = mostly no,
 *  null = reviewed but this key unanswered. */
export interface SeatGwc {
  getsIt: boolean | null;
  wantsIt: boolean | null;
  capacity: boolean | null;
}

interface Tally {
  getsIt: [number, number];
  wantsIt: [number, number];
  capacity: [number, number];
}

function verdict([yes, no]: [number, number]): boolean | null {
  if (yes === 0 && no === 0) return null;
  return yes >= no;
}

/**
 * Aggregate a closed round's raw scores into a per-subject-membership GWC
 * verdict. Only memberships that received at least one score appear in the
 * map — missing subjects get NO indicator (never fabricate a verdict).
 */
export function aggregateGwcByMembership(scores: PaScore[]): Map<string, SeatGwc> {
  const tallies = new Map<string, Tally>();
  for (const s of scores) {
    const key = s.subjectMembershipId;
    const t = tallies.get(key) ?? ({ getsIt: [0, 0], wantsIt: [0, 0], capacity: [0, 0] } satisfies Tally);
    if (s.getsIt === true) t.getsIt[0] += 1;
    else if (s.getsIt === false) t.getsIt[1] += 1;
    if (s.wantsIt === true) t.wantsIt[0] += 1;
    else if (s.wantsIt === false) t.wantsIt[1] += 1;
    if (s.capacity === true) t.capacity[0] += 1;
    else if (s.capacity === false) t.capacity[1] += 1;
    tallies.set(key, t);
  }

  const out = new Map<string, SeatGwc>();
  for (const [key, t] of tallies) {
    out.set(key, {
      getsIt: verdict(t.getsIt),
      wantsIt: verdict(t.wantsIt),
      capacity: verdict(t.capacity),
    });
  }
  return out;
}

// ── Department dot colour ─────────────────────────────────────────────────

const DOT_COLORS = [
  '#2D7FF9', // cerulean-ish
  '#7C3AED',
  '#059669',
  '#D97706',
  '#DB2777',
  '#0891B2',
  '#65A30D',
  '#DC2626',
];

/** Deterministic dot colour for a department id (stable across renders). */
export function departmentDotColor(departmentId: string | null): string {
  if (!departmentId) return '#94A3B8'; // slate — "no department"
  let hash = 0;
  for (let i = 0; i < departmentId.length; i++) {
    hash = (hash * 31 + departmentId.charCodeAt(i)) >>> 0;
  }
  return DOT_COLORS[hash % DOT_COLORS.length] ?? '#94A3B8';
}
